/**
 * @module bottomSheetService
 *
 * @description
 * 선언형 바텀시트 서비스입니다.
 * Provider 없이 `import bottomSheet from '~/services/bottomSheetService'`로 어디서든 사용할 수 있습니다.
 *
 * ## 핵심 개념
 *
 * - **싱글턴 패턴**: React Context 없이 외부 store로 bottomSheet 상태를 관리합니다.
 * - **고정 ID**: 컴포넌트에 `id` prop을 전달하면 같은 id의 entry를 재사용합니다. DOM에 중복으로 쌓이지 않습니다.
 * - **onClose / exit 2단계 라이프사이클**:
 *   - `onClose(value?)` → `open=false`로 전환하여 닫힘 애니메이션을 트리거합니다. (DOM 유지)
 *   - `exit()` → DOM에서 완전히 제거합니다. 필요한 경우에만 호출하세요.
 * - **Promise 기반 비동기 지원**: `onClose(value)`로 전달한 값을 `await`로 받을 수 있습니다.
 *
 * ## 사용법
 *
 * ### 1. 기본 사용 (고정 ID - 권장)
 * ```tsx
 * bottomSheet.open(({ open, onClose }) => (
 *   <ShuttleScheduleBottomSheet open={open} onClose={onClose} id="shuttle-schedule" />
 * ));
 * ```
 * 컴포넌트의 `id` prop이 자동으로 서비스의 entry ID로 사용됩니다.
 * 같은 id로 여러 번 호출해도 DOM에 1개만 존재하고, open 상태만 토글됩니다.
 *
 * ### 2. 비동기 사용 (닫힐 때까지 대기)
 * ```tsx
 * const result = await bottomSheet.open<string>(({ open, onClose }) => (
 *   <ConfirmBottomSheet open={open} onClose={onClose} id="confirm" />
 * ));
 * ```
 *
 * ### 3. 자동 ID (id 미지정 시 매번 새 entry 생성)
 * ```tsx
 * bottomSheet.open(({ open, onClose }) => (
 *   <SomeBottomSheet open={open} onClose={onClose} />
 * ));
 * ```
 * id를 지정하지 않으면 자동 생성되며, close 후 자동으로 DOM에서 제거됩니다.
 *
 * ### 4. 외부에서 제어 (BottomSheetHandle)
 * ```tsx
 * const handle = bottomSheet.open(({ open, onClose }) => (
 *   <TimerBottomSheet open={open} onClose={onClose} id="timer" />
 * ));
 *
 * setTimeout(() => handle.close(), 3000);
 * ```
 *
 * ## 주의사항
 *
 * - `BottomSheetProvider` 컴포넌트가 App에 마운트되어 있어야 합니다.
 * - `onClose()`는 DOM을 유지하고 `open=false`만 전환합니다. 같은 id로 다시 `open()`하면 재사용됩니다.
 * - `exit()`는 DOM에서 완전히 제거합니다. 재사용이 불필요한 경우에만 호출하세요.
 * - id를 지정하지 않은 경우(자동 ID), `onClose()` 후 자동으로 `exit()`가 호출됩니다.
 * - `onClose()`는 한 번만 resolve됩니다. 중복 호출 시 첫 번째 값만 반영됩니다.
 */
import { Fragment, type ReactNode, isValidElement, useSyncExternalStore } from 'react';

type BottomSheetId = string;
type Listener = () => void;

/** bottomSheet.open의 render 함수에 주입되는 controller */
export interface BottomSheetController<T = unknown> {
  /** 고정 ID. 명시하면 해당 값을 우선 사용하고, 미지정 시 컴포넌트명에서 자동 추론합니다. */
  id?: string;
  /** 현재 열림 상태. false가 되면 닫힘 애니메이션이 시작됩니다. */
  open: boolean;
  /** bottomSheet를 닫습니다. value를 전달하면 await의 반환값이 됩니다. DOM은 유지됩니다. */
  onClose: (value?: T) => void;
  /** bottomSheet를 DOM에서 완전히 제거합니다. 재사용이 불필요한 경우에만 호출하세요. */
  exit?: () => void;
}

/** bottomSheet.open의 반환값. 외부에서 bottomSheet를 제어하거나 await할 수 있습니다. */
export interface BottomSheetHandle<T = unknown> {
  close: () => void;
  exit: () => void;
  /** Promise처럼 await할 수 있습니다. onClose(value)의 value가 resolve됩니다. */
  then: Promise<T>['then'];
}

export type BottomSheetElement<T = unknown> = (controller: BottomSheetController<T>) => ReactNode;

interface BottomSheetEntry {
  id: BottomSheetId;
  rev: number;
  element: ReactNode;
}

let idCounter = 0;
let revCounter = 0;
let bottomSheets: BottomSheetEntry[] = [];
const listeners = new Set<Listener>();

/** 구독 중인 모든 리스너에게 상태 변경을 알립니다. */
const emitChange = () => {
  listeners.forEach((listener) => listener());
};

/** bottomSheet를 목록에서 제거합니다. (DOM에서 완전 제거) */
const remove = (id: BottomSheetId) => {
  bottomSheets = bottomSheets.filter((o) => o.id !== id);
  emitChange();
};

/** bottomSheet를 목록에 추가하거나 갱신합니다. */
const mountElement = (id: BottomSheetId, element: ReactNode) => {
  bottomSheets = bottomSheets.filter((o) => o.id !== id).concat({ id, rev: ++revCounter, element });
  emitChange();
};

/** PascalCase를 kebab-case로 변환합니다. */
const toKebabCase = (str: string): string => str.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();

/**
 * ReactElement에서 id를 추출합니다.
 * 1. props에 명시적 id가 있으면 사용
 * 2. 없으면 컴포넌트 함수명(displayName 또는 name)을 kebab-case로 변환하여 사용
 */
const extractIdFromElement = (element: ReactNode): string | undefined => {
  if (!isValidElement(element)) return undefined;

  const explicitId = (element.props as Record<string, unknown>).id as string | undefined;
  if (explicitId) return explicitId;

  const type = element.type as { displayName?: string; name?: string };
  const componentName = type.displayName ?? type.name;
  if (componentName) return toKebabCase(componentName);

  return undefined;
};

const bottomSheet = {
  /**
   * bottomSheet를 엽니다.
   * render 함수가 반환하는 컴포넌트에 `id` prop이 있으면 해당 id로 entry를 관리합니다.
   * 같은 id로 다시 open하면 기존 entry를 재사용하여 DOM에 중복 생성되지 않습니다.
   *
   * @param render - BottomSheetController를 받아 ReactNode를 반환하는 함수
   * @returns BottomSheetHandle - 외부 제어 및 await 가능
   */
  open: <T = unknown,>(render: BottomSheetElement<T>): BottomSheetHandle<T> => {
    // render를 한 번 호출하여 컴포넌트의 id prop을 추출
    const probe = render({ id: '', open: true, onClose: () => {} });
    const id = extractIdFromElement(probe) ?? `bottom-sheet-${++idCounter}`;

    let resolve: (value: T) => void;

    const promise = new Promise<T>((res) => {
      resolve = res;
    });

    /**
     * open 상태를 변경하여 bottomSheet를 다시 렌더링합니다.
     * onClose() 호출 시 open=false로 재렌더 → 닫힘 애니메이션 트리거
     */
    const mountWithState = (isOpen: boolean, _value?: T) => {
      mountElement(
        id,
        render({
          id,
          open: isOpen,
          onClose: (val?: T) => {
            resolve(val as T);
            mountWithState(false, val);
          },
          exit: () => remove(id),
        })
      );
    };

    mountWithState(true);

    return {
      close: () => {
        resolve(undefined as T);
        mountWithState(false);
      },
      exit: () => remove(id),
      then: promise.then.bind(promise),
    };
  },

  /** useSyncExternalStore용 구독 함수 (BottomSheetProvider에서 사용) */
  subscribe: (listener: Listener) => {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },

  /** useSyncExternalStore용 스냅샷 반환 함수 (BottomSheetProvider에서 사용) */
  getSnapshot: () => bottomSheets,
};

/**
 * @description
 * bottomSheetService의 상태를 구독하여 bottomSheet 목록을 렌더링하는 컴포넌트입니다.
 * App.tsx에 1회 마운트하면 bottomSheet.open()으로 생성된 모든 bottomSheet가 이 컴포넌트를 통해 렌더링됩니다.
 */
export const BottomSheetProvider = () => {
  const entries = useSyncExternalStore(bottomSheet.subscribe, bottomSheet.getSnapshot);
  return (
    <>
      {entries.map(({ id, rev, element }) => (
        <Fragment key={`${id}-${rev}`}>{element}</Fragment>
      ))}
    </>
  );
};

export default bottomSheet;
